<template>
  <div class="confetti">
    <span
      v-for="piece in confetti"
      :key="piece.id"
      :style="{
        left: `${piece.left}%`,
        background: piece.color,
        animationDelay: `${piece.delay}s`,
      }"
    ></span>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'

const COLORS = ['#ffc906', '#51b06f', '#209bf5', '#ff7124', '#f3be00']

interface ConfettiPiece {
  id: number
  left: number
  color: string
  delay: number
}

const props = withDefaults(
  defineProps<{
    burstOnMount?: boolean
  }>(),
  {
    burstOnMount: true,
  },
)

onMounted(() => {
  if (props.burstOnMount) {
    burst()
  }
})

onUnmounted(() => {
  clearTimeout(cleanupTimeout.value)
})

const confetti = ref<ConfettiPiece[]>([])
const cleanupTimeout = ref<number>()

function burst(): void {
  confetti.value = Array.from({ length: 30 }, (_, index) => ({
    id: index,
    left: 6 + Math.random() * 88,
    color: COLORS[index % COLORS.length],
    delay: Math.random() * 0.3,
  }))

  // Cleanup the confetti particles after the animation is done.
  cleanupTimeout.value = setTimeout(() => {
    confetti.value = []
  }, 3000)
}

defineExpose({
  burst,
})
</script>

<style lang="scss" scoped>
.confetti {
  position: absolute;
  top: -6px;
  left: 0;
  right: 0;
  height: 0;
  pointer-events: none;

  span {
    position: absolute;
    width: 10px;
    height: 10px;
    border-radius: 2px;
    opacity: 0;
    --targetY: 150px;
    animation: confetti 3s linear(0, 0.7 15%, 1.05 33%, 1.1 50%, 1) forwards;

    @media (max-width: $w-mobile-s) {
      width: 5px;
      height: 5px;
      --targetY: 100px;
    }
  }
}

@keyframes confetti {
  0% {
    opacity: 1;
    transform: translateY(-8px) rotate(0);
  }
  100% {
    opacity: 0;
    transform: translateY(var(--targetY)) rotate(280deg);
  }
}
</style>
